import { UnclaimedRecordMarker } from '../imported/UnclaimedRecordMarker';
import {
  unclaimedClinicianCopy,
  type UnclaimedClinicianVariant,
} from './unclaimedClinicianCopy';

export type { UnclaimedClinicianVariant };

/**
 * A clinician name an imported chart already points at (EMR-1264).
 *
 * The Kinnser import pre-creates a staff row so a migrated chart can name who
 * did the work. Nobody has signed in to that row, so it appears in no picker
 * and can take no new assignment. Where the assignment already exists the name
 * still shows — hiding it would lose the only record of who saw the patient.
 * The warning border and the badge say the name is history, not staffing.
 *
 * When `unclaimed` is false this renders the bare name, so call sites can use
 * it wherever an assigned clinician appears without branching.
 */
export function UnclaimedClinicianName({
  name,
  unclaimed,
  variant = 'assignment',
  detailLines,
  action,
  testId = 'unclaimed-clinician',
}: {
  name: string;
  unclaimed: boolean;
  variant?: UnclaimedClinicianVariant;
  /** Lines the surface appends after the variant's own; see the copy module. */
  detailLines?: string[];
  /** What the reader can do here, rendered under the lines. */
  action?: React.ReactNode;
  testId?: string;
}) {
  if (!unclaimed) {
    return <>{name}</>;
  }

  const { popoverLabel, lines } = unclaimedClinicianCopy(
    variant,
    name,
    detailLines
  );

  return (
    <UnclaimedRecordMarker
      popoverLabel={popoverLabel}
      lines={lines}
      action={action}
      className='whitespace-nowrap'
      testId={testId}
    >
      <span className='truncate'>{name}</span>
    </UnclaimedRecordMarker>
  );
}

export default UnclaimedClinicianName;
